import { useId } from 'react'
import { useEquipmentScope } from './equipmentScope'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { AssignableUser } from '@/api/equipmentApi'

const NONE = 'none'

/**
 * Who a generated PM task for this equipment type is assigned to
 * (`generateEvents`). Shared by the add and edit type dialogs so the wording
 * and the "no default" behaviour are stated once.
 *
 * `undefined`/`NONE` means no default — generated tasks stay unassigned
 * (surfaced in the worklist's Unassigned lane, not dropped; see z8nrz7dyfp).
 */
export function DefaultAssigneeField({
  value,
  onChange,
  users,
}: {
  value: string | undefined
  onChange: (value: string | undefined) => void
  users: AssignableUser[]
}) {
  const { copy } = useEquipmentScope()
  // Radix renders the trigger as a <button>, which a <label> cannot name: a
  // label's `htmlFor` only labels a labelable element, and wrapping a button in
  // one just adds a second activation. `aria-labelledby` pointing at the visible
  // text is the association Radix documents, so the control has one accessible
  // name and it is the same words the operator can see.
  const labelId = useId()
  const hintId = useId()
  return (
    <div className='space-y-1'>
      <span id={labelId} className='block text-xs font-medium text-gray-600'>
        Default Technician
      </span>
      <Select
        value={value ?? NONE}
        onValueChange={(v) => onChange(v === NONE ? undefined : v)}
      >
        <SelectTrigger className='w-full' aria-labelledby={labelId} aria-describedby={hintId}>
          <SelectValue placeholder='Unassigned' />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>None (unassigned)</SelectItem>
          {users.map((u) => (
            <SelectItem key={u.id} value={u.id}>
              {u.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p id={hintId} className='text-[11px] text-muted-foreground'>
        {copy.assigneeHint}
      </p>
    </div>
  )
}
